import Vue from 'vue'

const STORAGE_KEY = 'abfm_cookie_consent_v1'
const CONSENT_VERSION = 1
const BANNER_DELAY_MS = 2000
const OPTIONAL_CATEGORIES = ['analytics', 'functional', 'thirdParty']

function buildDefaultConsent() {
  return {
    necessary: true,
    analytics: false,
    functional: false,
    thirdParty: false,
    hasConsented: false,
    acceptedAt: null,
    updatedAt: null,
    version: CONSENT_VERSION,
  }
}

function normalizeConsent(input) {
  const defaults = buildDefaultConsent()
  if (!input || typeof input !== 'object') return defaults

  const acceptedAt = input.acceptedAt || null
  const updatedAt = input.updatedAt || acceptedAt || null

  return {
    necessary: true,
    analytics: Boolean(input.analytics),
    functional: Boolean(input.functional),
    thirdParty: Boolean(input.thirdParty),
    hasConsented: Boolean(input.hasConsented),
    acceptedAt,
    updatedAt,
    version: CONSENT_VERSION,
  }
}

function readStoredConsent() {
  if (typeof window === 'undefined' || !window.localStorage) return null

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return normalizeConsent(JSON.parse(raw))
  } catch (error) {
    return null
  }
}

function storeConsent(consent) {
  if (typeof window === 'undefined' || !window.localStorage) return

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent))
  } catch (error) {}
}

export const cookieConsentState = Vue.observable({
  isReady: false,
  showBanner: false,
  showPreferences: false,
  consent: buildDefaultConsent(),
})

const consentListeners = new Set()
const categoryListeners = OPTIONAL_CATEGORIES.reduce((accumulator, category) => {
  accumulator[category] = new Set()
  return accumulator
}, {})

let initialized = false
const loadedScripts = new Map()
let bannerTimer = null

function notifyListeners(previousConsent, nextConsent) {
  consentListeners.forEach((listener) => {
    listener(nextConsent, previousConsent)
  })

  OPTIONAL_CATEGORIES.forEach((category) => {
    if (!previousConsent[category] && nextConsent[category]) {
      categoryListeners[category].forEach((listener) => {
        listener(nextConsent)
      })
    }
  })
}

function updateConsent(nextPartialConsent, { trackFirstAcceptance = false } = {}) {
  const previousConsent = { ...cookieConsentState.consent }
  const timestamp = new Date().toISOString()
  const existingAcceptedAt = cookieConsentState.consent.acceptedAt
  const acceptedAt = existingAcceptedAt || (trackFirstAcceptance ? timestamp : null)

  if (bannerTimer) {
    clearTimeout(bannerTimer)
    bannerTimer = null
  }

  const merged = normalizeConsent({
    ...cookieConsentState.consent,
    ...nextPartialConsent,
    hasConsented: true,
    necessary: true,
    updatedAt: timestamp,
    acceptedAt,
  })

  cookieConsentState.consent = merged
  cookieConsentState.showBanner = false
  cookieConsentState.showPreferences = false
  storeConsent(merged)
  notifyListeners(previousConsent, merged)
}

export function initCookieConsent() {
  if (initialized) return cookieConsentState
  initialized = true

  const stored = readStoredConsent()
  if (stored) {
    cookieConsentState.consent = stored
    cookieConsentState.showBanner = false
    if (!stored.hasConsented) {
      bannerTimer = setTimeout(() => {
        if (!cookieConsentState.consent.hasConsented) {
          cookieConsentState.showBanner = true
        }
      }, BANNER_DELAY_MS)
    }
  } else {
    cookieConsentState.consent = buildDefaultConsent()
    cookieConsentState.showBanner = false
    bannerTimer = setTimeout(() => {
      if (!cookieConsentState.consent.hasConsented) {
        cookieConsentState.showBanner = true
      }
    }, BANNER_DELAY_MS)
  }
  cookieConsentState.isReady = true

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return
      const latest = readStoredConsent()
      if (!latest) return

      const previousConsent = { ...cookieConsentState.consent }
      cookieConsentState.consent = latest
      cookieConsentState.showBanner = !latest.hasConsented
      if (latest.hasConsented && bannerTimer) {
        clearTimeout(bannerTimer)
        bannerTimer = null
      }
      notifyListeners(previousConsent, latest)
    })
  }

  return cookieConsentState
}

export function isCategoryAllowed(category) {
  if (category === 'necessary') return true
  return Boolean(cookieConsentState.consent[category])
}

export function openCookiePreferences() {
  cookieConsentState.showPreferences = true
}

export function closeCookiePreferences() {
  cookieConsentState.showPreferences = false
}

export function acceptAllCookies() {
  updateConsent(
    {
      analytics: true,
      functional: true,
      thirdParty: true,
    },
    { trackFirstAcceptance: true }
  )
}

export function rejectNonEssentialCookies() {
  updateConsent(
    {
      analytics: false,
      functional: false,
      thirdParty: false,
    },
    { trackFirstAcceptance: true }
  )
}

export function saveCookiePreferences(preferences = {}) {
  updateConsent(
    {
      analytics: Boolean(preferences.analytics),
      functional: Boolean(preferences.functional),
      thirdParty: Boolean(preferences.thirdParty),
    },
    { trackFirstAcceptance: true }
  )
}

export function onConsentChange(listener, { immediate = false } = {}) {
  if (typeof listener !== 'function') return () => {}
  consentListeners.add(listener)
  if (immediate) listener(cookieConsentState.consent, null)
  return () => consentListeners.delete(listener)
}

export function onCategoryAccepted(
  category,
  listener,
  { once = false, immediate = true } = {}
) {
  if (!OPTIONAL_CATEGORIES.includes(category) || typeof listener !== 'function') {
    return () => {}
  }

  if (immediate && isCategoryAllowed(category)) {
    listener(cookieConsentState.consent)
    if (once) return () => {}
  }

  const listeners = categoryListeners[category]
  const wrapped = once
    ? (consent) => {
        listener(consent)
        listeners.delete(wrapped)
      }
    : listener

  listeners.add(wrapped)
  return () => listeners.delete(wrapped)
}

export function loadScriptOnce({ id, src, async = true, defer = true, attributes = {} }) {
  if (typeof document === 'undefined' || !src) {
    return Promise.resolve(null)
  }

  const key = id || src
  if (loadedScripts.has(key)) return loadedScripts.get(key)

  const existing =
    (id && document.getElementById(id)) ||
    document.querySelector(`script[src="${src}"]`)

  if (existing) {
    const readyPromise = Promise.resolve(existing)
    loadedScripts.set(key, readyPromise)
    return readyPromise
  }

  const script = document.createElement('script')
  if (id) script.id = id
  script.src = src
  script.async = async
  script.defer = defer
  script.setAttribute('data-cookie-managed', 'true')

  Object.keys(attributes).forEach((attributeName) => {
    if (attributes[attributeName] === undefined || attributes[attributeName] === null) return
    script.setAttribute(attributeName, String(attributes[attributeName]))
  })

  const promise = new Promise((resolve, reject) => {
    script.onload = () => resolve(script)
    script.onerror = (error) => reject(error)
  })

  loadedScripts.set(key, promise)
  document.head.appendChild(script)
  return promise
}

export function loadScriptForCategory(category, options) {
  if (!isCategoryAllowed(category)) return Promise.resolve(null)
  return loadScriptOnce(options)
}

export function loadScriptAfterConsent(category, options) {
  if (isCategoryAllowed(category)) return loadScriptOnce(options)

  return new Promise((resolve, reject) => {
    onCategoryAccepted(
      category,
      () => {
        loadScriptOnce(options).then(resolve).catch(reject)
      },
      { once: true, immediate: false }
    )
  })
}

export function initGoogleAnalytics(measurementId) {
  if (typeof window === 'undefined' || !measurementId) return Promise.resolve(null)
  if (
    window.__abfmGaInitialized &&
    window.__abfmGaMeasurementId &&
    window.__abfmGaMeasurementId === measurementId
  ) {
    return Promise.resolve(window.gtag || null)
  }

  window.dataLayer = window.dataLayer || []
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments)
    }

  window.gtag('js', new Date())

  return loadScriptOnce({
    id: 'ga-script',
    src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`,
    async: true,
    defer: true,
  }).then(() => {
    window.gtag('config', measurementId, { anonymize_ip: true })
    window.__abfmGaInitialized = true
    window.__abfmGaMeasurementId = measurementId
    return window.gtag
  })
}

export function trackGoogleAnalyticsPageView(path) {
  if (
    typeof window === 'undefined' ||
    typeof window.gtag !== 'function' ||
    !window.__abfmGaMeasurementId
  ) {
    return
  }

  const pagePath = path || `${window.location.pathname}${window.location.search}`
  window.gtag('event', 'page_view', { page_path: pagePath })
}

// Placeholder helper: if OTA script is strictly required for booking flow, pass treatAsNecessary: true.
export function loadOtaSyncScript(scriptUrl, { treatAsNecessary = true } = {}) {
  if (!scriptUrl) return Promise.resolve(null)
  const category = treatAsNecessary ? 'necessary' : 'thirdParty'
  return loadScriptAfterConsent(category, {
    id: 'ota-sync-script',
    src: scriptUrl,
    async: true,
    defer: true,
  })
}

// Placeholder helper: if OPPWA/Repayed script is strictly required for payment flow, pass treatAsNecessary: true.
export function loadOppwaScript(scriptUrl, { treatAsNecessary = true } = {}) {
  if (!scriptUrl) return Promise.resolve(null)
  const category = treatAsNecessary ? 'necessary' : 'thirdParty'
  return loadScriptAfterConsent(category, {
    id: 'oppwa-script',
    src: scriptUrl,
    async: true,
    defer: true,
  })
}
